/**
 * root route の `shouldRevalidate` 判定（PRODUCT_DEV-702 / 703）
 *
 * root-loader は whoami + 設定系 6 本（presets 261kB / onp-prompt-settings 97.6kB 等）を
 * 取得し、react-router の loader は**ナビゲーションをブロックする**。
 * つまり「遷移のときに再検証する = その遷移が必ず数百 ms 固まる」ということで、
 * 再検証を遷移に紐づける設計自体が UX と相性が悪い。
 *
 * - PRODUCT_DEV-652: search params のみの変化を止めた（pathname 変化は残っていた）
 * - PRODUCT_DEV-702: pathname 変化も止め、`/admin` の出入り・内部移動だけ再検証にした
 * - PRODUCT_DEV-703: その `/admin` 遷移が「画面が止まった」ように見えたため、
 *   **ナビゲーション起点の再検証を全廃**した
 *
 * 代わりに「マスタを書き換える API が成功したとき」に `useRevalidator()` で取り直す。
 * 判定と発火は `loaders/rootMasters.ts` + `hooks/useApi.ts` にあり、そちらは遷移を
 * ブロックしない（現在のデータのまま描画され、届いた時点で差し替わる）。
 *
 * ⚠ **ここに「この画面に入ったら再検証」を足さないこと。** 足した瞬間その遷移が
 * ブロッキングになり、本課題の症状が再発する。鮮度が要るなら書き込み側
 * （`rootMasters.ts` の対象エンドポイント）に足す。
 */
export interface RootRevalidateArgs {
  currentUrl: URL;
  nextUrl: URL;
  defaultShouldRevalidate: boolean;
  formMethod?: string;
}

export function shouldRevalidateRoot({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
  formMethod,
}: RootRevalidateArgs): boolean {
  // action 経由（非 GET のサブミット）の再検証は常に尊重する。
  // 現状 action を持つルートは無いが、将来足したときに静かに効かなくなるのを防ぐ。
  if (formMethod && formMethod.toUpperCase() !== "GET") return true;

  // `useRevalidator().revalidate()` は currentUrl === nextUrl で呼ばれ、
  // このとき defaultShouldRevalidate は true になる。書き込み後の取り直しはここを通る。
  if (currentUrl.href === nextUrl.href) return defaultShouldRevalidate;

  // 純粋な画面遷移では取り直さない
  return false;
}
